import { useCheckPermission } from "@/hook/use-check-permission"
import { t } from "@/languages/i18n"
import { useLanguage } from "@/languages/LanguageProvider"
import { createLetaky as createLetakyService, createLocalization, deleteLetaky, fetchLetaky, getLetakyById, updateLetaky as updateLetakyService } from "@/services/letaky"
import { LetakRequest, LetakResponseDataObject } from "@/types/codegen/data-contracts"
import { RequestParams } from "@/types/codegen/http-client"
import { LetakyData, Pagination } from "@/types/types"
import { App, TablePaginationConfig } from "antd"
import { useCallback, useMemo, useState } from "react"

interface UseLetakyOptions {
  searchTerm?: string
  refreshKey?: number
}

interface UseLetakyReturn {
  letaky: LetakyData[]
  loading: boolean
  error: string | null
  pagination: Pagination
  fetchLetakyData: (searchParams?: { [key: string]: string }, publicationState?: string) => Promise<any>
  handleTableChange: (pagination: TablePaginationConfig) => void
  handleDelete: (id: number) => Promise<void>
  hasPermission: (action: string) => boolean
}

export const useLetaky = (options: UseLetakyOptions = {}) => {
  const { searchTerm = "" } = options
  const { language } = useLanguage()
  const { message } = App.useApp()
  const { hasPermission: checkPermission } = useCheckPermission()

  // Get current locale from language context
  const currentLocale = useMemo(() => {
    return language || "vi"
  }, [language])

  const [letaky, setLetaky] = useState<LetakyData[]>([])
  const [letakyDetail, setLetakyDetail] = useState<LetakResponseDataObject | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: 10,
    total: 0,
    current: 1,
  })

  const fetchLetakyData = useCallback(
    async (searchParams: { [key: string]: string } = {}, publicationState: string = "preview") => {
      const params: { [key: string]: string } = { ...searchParams }

      // Set locale if not provided
      if (!params.locale) {
        params.locale = currentLocale
      }

      if (searchTerm) {
        params.title = searchTerm
      }

      // Use current pagination state if not provided in searchParams
      if (!params.page) {
        params.page = String(pagination.page)
      }
      if (!params.pageSize) {
        params.pageSize = String(pagination.pageSize)
      }

      try {
        setLoading(true)
        setError(null)
        const response = await fetchLetaky(params, publicationState)
        if (response?.error) {
          setError(response.error.message)
          return response
        }

        setLetaky(response?.data?.length > 0 ? response.data : [])
        setPagination(prev => ({
          ...prev,
          total: response?.meta?.pagination?.total ?? 0,
          current: response?.meta?.pagination?.page ?? 1,
          pageSize: response?.meta?.pagination?.pageSize ?? prev.pageSize,
        }))

        return response
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Unknown error"
        setError(errorMessage)
        message.error(t("Lấy dữ liệu letaky thất bại!"))
        throw err
      } finally {
        setLoading(false)
      }
    },
    [searchTerm, pagination.page, pagination.pageSize, currentLocale],
  )

  const handleTableChange = useCallback(
    (paginationConfig: TablePaginationConfig, filters?: any, sorter?: any) => {
      const newPagination = {
        current: paginationConfig.current || 1,
        pageSize: paginationConfig.pageSize || 10,
        total: paginationConfig.total || 0,
      }

      setPagination(prev => ({
        ...prev,
        ...newPagination,
      }))

      const params: { [key: string]: string } = {
        page: String(newPagination.current),
        pageSize: String(newPagination.pageSize),
      }

      // Handle sorting
      if (sorter && !Array.isArray(sorter) && sorter.field && sorter.order) {
        const field = Array.isArray(sorter.field) ? sorter.field[1] : sorter.field
        const order = sorter.order === "ascend" ? "asc" : "desc"
        params.sort = `${field}:${order}`
      }

      // Fetch data with new pagination and sorting
      fetchLetakyData(params)
    },
    [fetchLetakyData],
  )

  const handleDelete = useCallback(
    async (id: number) => {
      try {
        setLoading(true)
        await deleteLetaky(id)
        message.success(t("Letaky được xóa thành công!"))
        // Refresh data after delete
        await fetchLetakyData()
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Unknown error"
        setError(errorMessage)
        message.error(t("Xóa letaky thất bại!"))
      } finally {
        setLoading(false)
      }
    },
    [fetchLetakyData],
  )

  const fetchLetakyById = useCallback(async (id: number): Promise<any | null> => {
    try {
      setLoading(true)
      setError(null)
      const queryParams = {
        populate: {
          title: true,
          description: true,
          featuredImage: true,
          image: true,
          catalogImages: true,
          pdfFile: true,
          pages: true,
          dateStart: true,
          dateEnd: true,
          status: true,
          createdAt: true,
          updatedAt: true,
          publishedAt: true,
          createdBy: true,
          updatedBy: true,
          publishedBy: true,
          locale: true,
          localization: true,
          website: true,
        },
        sort: "createdAt:desc",
      }
      const response = await getLetakyById(Number(id), queryParams as RequestParams)

      if (response?.data) {
        setLetakyDetail(response.data as LetakResponseDataObject)
        return response.data
      }

      return null
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error"
      setError(errorMessage)
      message.error(t("Lấy dữ liệu letaky thất bại!"))
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  const createLetaky = useCallback(async (data: LetakRequest): Promise<boolean> => {
    try {
      setLoading(true)
      setError(null)
      await createLetakyService(data)
      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error"
      setError(errorMessage)

      return false
    } finally {
      setLoading(false)
    }
  }, [])

  const updateLetaky = useCallback(async (id: number, data: LetakRequest): Promise<boolean> => {
    try {
      setLoading(true)
      setError(null)
      await updateLetakyService(id, data)
      message.success(t("Cập nhật letaky thành công!"))
      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error"
      setError(errorMessage)
      message.error(t("Cập nhật letaky thất bại!"))
      return false
    } finally {
      setLoading(false)
    }
  }, [])

  const hasPermission = useCallback((action: string) => {
    return checkPermission("letak", action)
  }, [checkPermission])

  const createLetakyLocalization = async (id: number, data: any) => {
    setLoading(true);
    try {
      const res = await createLocalization(id, data);

      if (res) {

        return res;
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    letaky,
    loading,
    error,
    pagination,
    fetchLetakyData,
    handleTableChange,
    handleDelete,
    hasPermission,
    fetchLetakyById,
    letakyDetail,
    updateLetaky,
    createLetaky,
    createLetakyLocalization
  }
}
